import * as Monaco from "monaco-editor";

// The palette this theme is baked from must be on the page even when nothing
// else on it renders highlighted code.
import "@/shared/foundation/styles/codePalette.css";

/**
 * Monaco theme that mirrors the app's highlight.js code theme, so the same
 * RoboQL query is coloured identically in a filter chip (highlight.js, styled
 * by `HighlightedCodeBody.css`) and in the editor.
 *
 * `monaco.editor.defineTheme` only takes hex, while our palette lives in CSS
 * custom properties expressed with `oklch()` and `light-dark()`. The values are
 * therefore resolved off the live document at define time (see
 * `resolveCodePalette`) and the theme is re-defined whenever the app's
 * light/dark mode flips.
 *
 * Styling Monaco from CSS instead would avoid all of that, but is not an
 * option: it bakes each colour into a generated class whose name comes from a
 * colour-map index that moves between themes. One theme name is reused for both modes: the palette
 * is read from whichever mode is current, and `defineTheme` on the active theme
 * refreshes every mounted editor.
 */
export const ROBOQL_MONACO_THEME = "roboql";

/** base16 slots consumed below; see `foundation/styles/codePalette.css`. */
const CODE_PALETTE_SLOTS = [
  "base00",
  "base01",
  "base02",
  "base03",
  "base04",
  "base05",
  "base08",
  "base09",
  "base0a",
  "base0b",
  "base0c",
  "base0d",
  "base0e",
] as const;

type CodePaletteSlot = (typeof CODE_PALETTE_SLOTS)[number];
type CodePalette = Record<CodePaletteSlot, string>;

/**
 * Fallback used when a slot cannot be resolved (no document, or a colour syntax
 * the canvas refuses to parse). Deliberately garish so a miss is obvious rather
 * than silently rendering as plain text.
 */
const UNRESOLVED = "#ff00ff";

/**
 * Resolve a CSS colour string to `#rrggbb`.
 *
 * The browser does the work: the value is painted onto a 1x1 canvas and the
 * pixel is read back, which collapses `oklch()`, `color-mix()`, named colours
 * and anything else CSS Color 4 admits into plain sRGB bytes.
 */
function toHex(
  ctx: CanvasRenderingContext2D,
  cssColor: string,
): string | undefined {
  // A rejected `fillStyle` assignment is a silent no-op, so prime with a
  // sentinel and treat "sentinel survived" as "the browser could not parse it".
  ctx.fillStyle = "#000000";
  ctx.fillStyle = cssColor;
  if (ctx.fillStyle === "#000000" && cssColor.trim() !== "#000000") {
    return undefined;
  }

  ctx.clearRect(0, 0, 1, 1);
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;

  return `#${[r, g, b]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

/**
 * Read every `--code-base*` slot off the live document as a hex string.
 *
 * `getComputedStyle().getPropertyValue()` on a custom property is not enough:
 * custom properties are untyped, so a `light-dark()` or `oklch(from …)` value
 * comes back as unresolved text. Assigning the property to a real `color` on a
 * probe element forces the browser to resolve it, and the canvas round-trip
 * turns the result into bytes.
 */
export function resolveCodePalette(): CodePalette {
  const palette = Object.fromEntries(
    CODE_PALETTE_SLOTS.map((slot) => [slot, UNRESOLVED]),
  ) as CodePalette;

  const ctx = document
    .createElement("canvas")
    .getContext("2d", { willReadFrequently: true });
  if (ctx === null) {
    return palette;
  }

  const probe = document.createElement("span");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  document.body.appendChild(probe);

  try {
    for (const slot of CODE_PALETTE_SLOTS) {
      probe.style.color = `var(--code-${slot})`;
      const resolved = toHex(ctx, window.getComputedStyle(probe).color);
      if (resolved !== undefined) {
        palette[slot] = resolved;
      }
    }
  } finally {
    probe.remove();
  }

  return palette;
}

/**
 * Monarch token (`./language.ts`) → base16 slot, mirroring the hljs scope →
 * slot mapping in `HighlightedCodeBody.css` so both renderers agree.
 */
function tokenRules(palette: CodePalette): Monaco.editor.ITokenThemeRule[] {
  return [
    // `.hljs` default foreground.
    { token: "", foreground: palette.base05 },
    // `.hljs-comment`
    { token: "comment", foreground: palette.base03, fontStyle: "italic" },
    // `.hljs-keyword`, `.hljs-literal` (which carry `font-weight: 600`).
    { token: "keyword", foreground: palette.base0e, fontStyle: "bold" },
    // `.hljs-variable` — the hljs RoboQL grammar scopes identifiers `variable`.
    { token: "identifier", foreground: palette.base08 },
    // `.hljs-type`
    { token: "type", foreground: palette.base0a },
    // `.hljs-number`. Prefix match also covers `number.float` / `number.hex`.
    { token: "number", foreground: palette.base09 },
    // `.hljs-string`. Prefix match also covers `string.quote`.
    { token: "string", foreground: palette.base0b },
    // `.hljs-regexp` shares the escape slot.
    { token: "string.escape", foreground: palette.base0c },
    { token: "string.invalid", foreground: palette.base08 },
    // `.hljs-operator`
    { token: "operator", foreground: palette.base0c },
    // Punctuation and brackets are unscoped in hljs, i.e. default foreground.
    { token: "delimiter", foreground: palette.base05 },
  ];
}

function editorColors(palette: CodePalette): Monaco.editor.IColors {
  return {
    "editor.background": palette.base00,
    "editor.foreground": palette.base05,
    "editorCursor.foreground": palette.base05,
    "editor.selectionBackground": palette.base02,
    "editor.lineHighlightBackground": palette.base01,
    "editorLineNumber.foreground": palette.base03,
    "editorLineNumber.activeForeground": palette.base04,
    // Defaults to the ghost-text colour, which the pinned `vs` base resolves
    // light in both modes -- near-black, and so invisible against a dark
    // editor. base16 spends base03 on comments and placeholders, which is the
    // muted-against-the-background reading this wants.
    "editor.placeholder.foreground": palette.base03,
    "editorWidget.background": palette.base00,
    "editorWidget.border": palette.base02,
    "editorSuggestWidget.background": palette.base00,
    "editorSuggestWidget.border": palette.base02,
    "editorSuggestWidget.foreground": palette.base05,
    "editorSuggestWidget.highlightForeground": palette.base0d,
    // Name a foreground beside every background: `base` is pinned to `vs`, so
    // anything unnamed falls back to VS Code's light defaults in both modes.
    "editorSuggestWidget.selectedBackground": palette.base02,
    "editorSuggestWidget.selectedForeground": palette.base05,
    "editorSuggestWidget.selectedIconForeground": palette.base05,
    "editorSuggestWidget.focusHighlightForeground": palette.base0d,
    "list.hoverBackground": palette.base01,
    "list.hoverForeground": palette.base05,
    "list.focusBackground": palette.base02,
    "list.focusForeground": palette.base05,
    "list.focusHighlightForeground": palette.base0d,
    "list.activeSelectionBackground": palette.base02,
    "list.activeSelectionForeground": palette.base05,
    "list.inactiveSelectionBackground": palette.base01,
    "list.inactiveSelectionForeground": palette.base05,
  };
}

/**
 * (Re-)define the RoboQL theme from the palette the document currently
 * resolves. Safe to call repeatedly: re-defining the active theme repaints
 * every mounted editor.
 *
 * The `base` is pinned to `vs` in both modes, which looks wrong but is load
 * bearing. Monarch bakes a *colour-map index*, not a colour, into each token at
 * tokenize time, and the index depends on which built-in rules the theme
 * inherits. Flipping the base between bakes shifts those indices under lines
 * that were already tokenized, so they end up painted in somebody else's
 * colour — observed as VS Code purples and reds surviving a light/dark toggle.
 * Holding the base fixed keeps the index list stable, so a re-bake only swaps
 * the colour behind each index and every mounted editor repaints correctly.
 *
 * The cost is that Monaco reports itself as a light theme in dark mode, so any
 * chrome colour `editorColors` does not name explicitly keeps its `vs` default.
 */
export function defineRoboqlTheme(monaco: typeof Monaco): void {
  const palette = resolveCodePalette();

  monaco.editor.defineTheme(ROBOQL_MONACO_THEME, {
    base: "vs",
    inherit: true,
    rules: tokenRules(palette),
    colors: editorColors(palette),
  });
}
